// AI for the CRM. Inside the ThnkOS CRM tab the request runs on the Mac's own Claude (postMessage bridge to
// the parent page); anywhere else, or when the bridge fails, it goes to Cloudflare Workers AI (/api/ai).
import type { z } from "zod";

import { extractJson } from "../../../../supabase/functions/_shared/leadAgent.ts";
import { getSupabaseClient } from "../providers/supabase/supabase";

export type AiTask =
  | "score_fit"
  | "classify_intent"
  | "extract_company"
  | "find_website"
  | "extract_milestones"
  | "lead_filters"
  | "lead_segments"
  | "rank_leads"
  | "draft_email"
  | "draft_reply"
  | "why_now";

const HELLO_TIMEOUT_MS = 500;
const BRIDGE_TIMEOUT_MS = 150_000; // local Claude may take one to two minutes
const SERVER_TIMEOUT_MS = 60_000;
export const AI_ENDPOINT = "/api/ai"; // vite proxies it to `wrangler dev` in development

/** Only a ThnkOS page on this machine or on the tailnet may answer for Claude */
export const isThnkosOrigin = (origin: string) => {
  try {
    const url = new URL(origin);
    if (url.origin !== origin) return false;
    if (url.protocol === "http:")
      return url.hostname === "127.0.0.1" || url.hostname === "localhost";
    return url.protocol === "https:" && url.hostname.endsWith(".ts.net");
  } catch {
    return false;
  }
};

const parentOrigin = () => {
  const ancestor = window.location.ancestorOrigins?.[0];
  if (ancestor) return ancestor;
  try {
    return document.referrer ? new URL(document.referrer).origin : null;
  } catch {
    return null;
  }
};

let bridge: Promise<string | null> | undefined;

/** The ThnkOS origin after a hello/ready handshake, or null. Asked once per page. */
const connectBridge = () =>
  (bridge ??= new Promise<string | null>((resolve) => {
    const origin = window.parent !== window ? parentOrigin() : null;
    if (!origin || !isThnkosOrigin(origin)) return resolve(null);
    const done = (value: string | null) => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
      resolve(value);
    };
    const onMessage = (event: MessageEvent) => {
      if (
        event.source === window.parent &&
        event.origin === origin &&
        event.data?.type === "thnkos.ready"
      )
        done(origin);
    };
    const timer = setTimeout(() => done(null), HELLO_TIMEOUT_MS);
    window.addEventListener("message", onMessage);
    window.parent.postMessage({ type: "thnkos.hello" }, origin);
  }));

const askBridge = (origin: string, task: AiTask, prompt: string) =>
  new Promise<string>((resolve, reject) => {
    const id = crypto.randomUUID();
    const done = (fn: () => void) => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
      fn();
    };
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (
        event.source !== window.parent ||
        event.origin !== origin ||
        data?.type !== "thnkos.ai.result" ||
        data.id !== id
      )
        return;
      if (data.ok && typeof data.text === "string")
        done(() => resolve(data.text));
      else done(() => reject(new Error(data.error || "ThnkOS AI failed")));
    };
    const timer = setTimeout(
      () => done(() => reject(new Error("ThnkOS AI timed out"))),
      BRIDGE_TIMEOUT_MS,
    );
    window.addEventListener("message", onMessage);
    window.parent.postMessage({ type: "thnkos.ai", id, task, prompt }, origin);
  });

/** Waits before retrying a request the edge rate-limited (429); a burst of lead-agent calls can trip it */
export const RETRY_AFTER_MS = [4_000, 12_000];

const askServer = async (
  task: AiTask,
  prompt: string,
  answered?: (model: string) => void,
) => {
  const { data } = await getSupabaseClient().auth.getSession();
  const send = () =>
    fetch(AI_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${data.session?.access_token ?? ""}`,
      },
      body: JSON.stringify({ task, prompt }),
      signal: AbortSignal.timeout(SERVER_TIMEOUT_MS),
    });
  let response = await send();
  for (const wait of RETRY_AFTER_MS) {
    if (response.status !== 429) break;
    await new Promise((resolve) => setTimeout(resolve, wait));
    response = await send();
  }
  if (!response.ok) throw new Error(`AI request failed (${response.status})`);
  const body = await response.json();
  if (typeof body?.text !== "string") throw new Error("AI answered no text");
  answered?.(typeof body.model === "string" ? body.model : "");
  return body.text as string;
};

/** Run a prompt: local Claude through ThnkOS when the CRM sits in its tab, else Workers AI; `answered` hears which model ("claude" for the bridge) */
export const runAi = async (
  task: AiTask,
  prompt: string,
  answered?: (model: string) => void,
) => {
  const origin = await connectBridge();
  if (origin) {
    try {
      const text = await askBridge(origin, task, prompt);
      answered?.("claude");
      return text;
    } catch (error) {
      console.warn("ThnkOS AI failed, using Workers AI", error);
    }
  }
  return askServer(task, prompt, answered);
};

/** runAi, then the answer parsed as JSON and validated: nothing unchecked reaches the app */
export const runAiJson = async <T extends z.ZodType>(
  task: AiTask,
  prompt: string,
  schema: T,
): Promise<z.infer<T>> => schema.parse(extractJson(await runAi(task, prompt)));

export { extractJson };
export {
  untrusted,
  UNTRUSTED_NOTE,
} from "../../../../supabase/functions/_shared/untrusted.ts";

/** For tests: forget the handshake result */
export const resetAiBridge = () => {
  bridge = undefined;
};
